// Refine light: a filed issue's open questions, inside its row
// of `MeetingReview`, under the deciding quote and above "Zitate (n)". The
// owner's page and the magic link use this one component:
//
//   owner  folded (a `<details>`), so Accept and Decline stay in view;
//          a link holder's answer says "über den Link beantwortet".
//   link   open: the customer is who knows best.
//
// Each question shows its meeting anchor (one of the issue's verified
// quotes), Loom's draft answer CLOSED where the meeting discussed the field
// ("Diesen Text übernehmen" / "Selbst formulieren", the widgets' own closed
// offer), and "Weiss ich nicht". An answer folds to "Beantwortet: …" with
// "Ändern". The controls are the refine's own (`AnswerWidget`): a text for
// role, means and ends, the list builder for the criteria; the control
// follows the field.
//
// The answer goes to Loom (`onAnswer`): it counts in refine, and the server
// writes it into the issue's "Aus dem Meeting" block. A failed save keeps the
// typed text and says "Antwort nicht gespeichert. Nochmal versuchen."; a
// saved answer the block did not take (`bodySync` failed) says "Gespeichert,
// nicht ins Ticket geschrieben" with "Erneut versuchen", which sends the
// same answer again.

import { useState } from "react";

import {
  meetingClock,
  type MeetingAnswerRequest,
  type MeetingIssueRowDto,
  type MeetingQuestionDto,
  type MeetingQuestionField,
} from "@loom/shared/meetings";
import { splitAcceptanceCriteria } from "@loom/shared/readiness";

import { usePlural, useT } from "../../i18n/index.js";
import { AnswerWidget } from "../widgets";

import type { ReviewMode } from "./MeetingReview";

/** Resolves true when the answer was saved. */
export type QuestionAnswerHandler = (
  row: MeetingIssueRowDto,
  field: MeetingQuestionField,
  body: MeetingAnswerRequest,
) => Promise<boolean>;

/** The questions still waiting for an answer (neither answered nor skipped). */
export const openQuestionsOf = (row: Pick<MeetingIssueRowDto, "questions">): MeetingQuestionDto[] =>
  (row.questions ?? []).filter((q) => q.answer === null);

/** An answer's rows (the criteria) or its one line. */
const answerRows = (q: MeetingQuestionDto, text: string): string[] =>
  q.widget === "list_builder" ? splitAcceptanceCriteria(text) : [text];

function QuestionItem({
  mode,
  row,
  q,
  onAnswer,
}: {
  readonly mode: ReviewMode;
  readonly row: MeetingIssueRowDto;
  readonly q: MeetingQuestionDto;
  readonly onAnswer: QuestionAnswerHandler;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // A failed save remounts the control with what was typed (`retry.n` keys it).
  const [retry, setRetry] = useState<{ readonly text: string; readonly n: number } | null>(null);
  // The last answer saved here: "Erneut versuchen" sends it again when the
  // issue's block did not take it.
  const [sent, setSent] = useState<MeetingAnswerRequest | null>(null);

  const send = (body: MeetingAnswerRequest): void => {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    void onAnswer(row, q.field, body)
      .then((ok) => {
        if (ok) {
          setEditing(false);
          setRetry(null);
          setSent(body);
        } else {
          setFailed(true);
          if ("text" in body) setRetry((r) => ({ text: body.text, n: (r?.n ?? 0) + 1 }));
        }
      })
      .finally(() => setSaving(false));
  };

  const answer = q.answer;
  const folded = answer !== null && !editing;
  const anchorKey = q.status === "needs_input" ? "meetings.question.anchorOpen" : "meetings.question.anchor";
  // Seed for the control: a failed attempt's text, else (on "Ändern") the
  // answer being changed. Loom's draft is never a seed: it is offered closed.
  const seed = retry?.text ?? (editing && answer?.text ? answer.text : undefined);
  const list = q.widget === "list_builder";

  return (
    <li
      className="meet-question"
      data-field={q.field}
      {...(answer === null ? { "data-question-open": "true" } : {})}
    >
      <p className="meet-question-text">{q.question}</p>
      {q.anchor !== null && (
        <p className="meet-meta">
          {t(anchorKey, { quote: q.anchor.text, clock: meetingClock(q.anchor.approxMs) })}
        </p>
      )}
      {folded ? (
        <div className="meet-question-answer">
          {answer.state === "answered" && answer.text !== null ? (
            <>
              <span className="meet-meta">{t("meetings.question.answered")}</span>
              {list ? (
                <ul>
                  {answerRows(q, answer.text).map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              ) : (
                <> {answer.text}</>
              )}
            </>
          ) : (
            <span className="meet-meta">{t("meetings.question.skipped")}</span>
          )}
          {mode === "owner" && answer.via === "link" && (
            <span className="meet-meta"> · {t("meetings.question.viaLink")}</span>
          )}{" "}
          <button type="button" className="focus-deeplink" onClick={() => setEditing(true)}>
            {t("meetings.question.change")}
          </button>
        </div>
      ) : (
        <>
          <AnswerWidget
            key={`${q.field}:${String(retry?.n ?? 0)}:${editing ? "edit" : "new"}`}
            field={q.field}
            onAnswer={(text) => send({ text })}
            ariaLabel={q.question}
            answerLabel={q.question}
            assentGuard="refuse"
            // Several questions on one page: none takes the caret by itself.
            autoFocus={editing}
            {...(list ? { placeholder: t("meetings.question.listPlaceholder") } : {})}
            {...(seed !== undefined
              ? list
                ? { initialRows: answerRows(q, seed) }
                : { initialValue: seed }
              : {})}
            {...(q.draft !== null && seed === undefined
              ? {
                  loomAnswer: list
                    ? { kind: "list" as const, rows: q.draft.split("\n"), source: "draft" as const }
                    : { kind: "text" as const, text: q.draft, source: "draft" as const },
                }
              : {})}
          />
          <div className="meet-question-actions">
            {!saving && (
              <button type="button" className="focus-deeplink" onClick={() => send({ skip: true })}>
                {t("meetings.question.skip")}
              </button>
            )}
            {editing && !saving && (
              <button type="button" className="focus-deeplink" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </button>
            )}
          </div>
        </>
      )}
      {saving && (
        <p className="meet-meta" role="status">
          {t("meetings.question.saving")}
        </p>
      )}
      {failed && !saving && (
        <p className="focus-error" role="alert">
          {t("meetings.question.saveFailed")}
        </p>
      )}
      {sent !== null && row.bodySync === "failed" && !saving && !failed && (
        <p className="focus-help" role="status">
          {t("meetings.question.bodyFailed")}{" "}
          <button type="button" className="focus-deeplink" onClick={() => send(sent)}>
            {t("meetings.action.retry")}
          </button>
        </p>
      )}
    </li>
  );
}

export function MeetingQuestions({
  mode,
  row,
  onAnswer,
}: {
  readonly mode: ReviewMode;
  readonly row: MeetingIssueRowDto;
  readonly onAnswer: QuestionAnswerHandler;
}) {
  const t = useT();
  const tn = usePlural();
  const questions = row.questions ?? [];
  if (questions.length === 0) return null;
  const open = openQuestionsOf(row).length;
  const list = (
    <ul className="meet-question-list">
      {questions.map((q) => (
        <QuestionItem key={q.field} mode={mode} row={row} q={q} onAnswer={onAnswer} />
      ))}
    </ul>
  );
  if (mode === "owner") {
    return (
      <details className="meet-fold meet-questions-block">
        <summary>
          {t("meetings.issue.openQuestions")}
          {open > 0 ? ` · ${tn("plural.meetingQuestionsOpen", open)}` : ""}
        </summary>
        {list}
      </details>
    );
  }
  return (
    <div className="meet-questions-block">
      <span className="meet-meta">{t("meetings.issue.openQuestions")}</span>
      {list}
    </div>
  );
}
